import React, { useState, useEffect, useRef } from 'react';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mediumDesktop, mobile, tablet } from 'helpers/utils/screensizes';
import Button from 'components/commercetools-ui/atoms/button';
import Typography from 'components/commercetools-ui/atoms/typography';
import Input from 'components/commercetools-ui/atoms/input';
import { useFormat } from 'helpers/hooks/useFormat';
import dynamic from 'next/dynamic';
import Dropdown, { Option } from 'components/commercetools-ui/atoms/dropdown';
import {
  Occupation_Options,
  Preferred_Contact_Method_Options,
  Purpose_of_Enquiry_Options,
  Source_Site,
} from 'helpers/constants/contactUs';
import { sdk } from 'sdk';
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_SITE_KEY } from 'helpers/constants/googleAnalytics';
import Modal from 'components/commercetools-ui/atoms/modal';
import { useParams, usePathname } from 'next/navigation';
import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import { HomeIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import Link from 'components/commercetools-ui/atoms/link';



const Markdown = dynamic(() => import('../../organisms/markdown'));

export type ContactProps = {
  data?: any;
};
interface ContactFormData {
  First_Name: string;
  Purpose_of_Enquiry: string;
  Email: string;
  Occupation: string;
  Preferred_Contact_Method: string;
  Street_Address: string;
  Message: string;
  Phone: string;
  Country: string;
  Source_Site: string;
}
const ContactSlider = ({ data }: React.PropsWithChildren<ContactProps>) => {
  const [isDesktopSize] = useMediaQuery(mediumDesktop);
  const [isTabletSize] = useMediaQuery(tablet);
  const { formatMessage } = useFormat({ name: 'common' });
  const contact = data?.sections?.[1]?.section_with_heading_description || '';
  const collectionStatement = data?.sections?.[2]?.section_with_heading_description || '';
  const PopUPModalCollectionStatement = data.sections[3].section_with_heading_description || '';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [collectionStatementChecked, setCollectionStatementChecked] = useState(false);
  const isLargeScreen = useMediaQuery(mobile);
  const [error, setError] = useState<string | null>(null);
  const [clearDropdowns, setClearDropdowns] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    First_Name: '',
    Purpose_of_Enquiry: '',
    Email: '',
    Occupation: '',
    Preferred_Contact_Method: '',
    Street_Address: '',
    Message: '',
    Phone: '',
    Country: '',
    Source_Site: '',
  });
  const [validationErrors, setValidationErrors] = useState({
    enquiry: false,
    firstName: '',
    email: '',
    streetAddress: '',
    phone: '',
    message: '',
    checkbox: false,
  });

  const currentURL = usePathname();
  const isContactUsPage = currentURL.includes('/contact');
  const [success, setSuccess] = useState('');
  const { locale } = useParams();
  const [reCaptchaError, setReCaptchaError] = useState('');
  const [hasError, setHasError] = useState(true);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const onChangeRecaptcha = (e: any) => {
    // on captcha change
    if(reCaptchaError && e){
        setReCaptchaError('')
    }
  };
  const extensions = sdk.composableCommerce;
  const handleValidations = (e: React.FormEvent<HTMLFormElement>) => {
    // let hasError = false;
    e.preventDefault();
    setHasError(false);
    const allowedSpecialCharacters = /^[a-zA-Z0-9\s\-@#.,&*_]*$/;
    const noSpecialCharacters = /^[a-zA-Z0-9\s]*$/;
    if (!formData.First_Name) {
      setValidationErrors((prevErrors) => ({ ...prevErrors, firstName: 'Please enter a valid first name.' }));
      setHasError(true);
    } else if (!noSpecialCharacters.test(formData.First_Name)) {
      setValidationErrors((prevErrors) => ({...prevErrors, firstName: 'First name should not have any special characters.'}));
      setHasError(true);
    } else {
      setValidationErrors((prevErrors) => ({ ...prevErrors, firstName: '' }));
    }

    if (!allowedSpecialCharacters.test(formData.Street_Address)) {
      setValidationErrors((prevErrors) => ({...prevErrors, streetAddress: 'Street Address has some invalid special characters. Only following special characters are allowed (-@#.*_)'}));
      setHasError(true);
    } else {
      setValidationErrors((prevErrors) => ({ ...prevErrors, streetAddress: '' }));
    }  

    if (!formData.Purpose_of_Enquiry) {
      setValidationErrors((prevErrors) => ({ ...prevErrors, enquiry: true }));
      setHasError(true);
    } else {
      setValidationErrors((prevErrors) => ({ ...prevErrors, enquiry: false }));
    }

    if (!formData.Message) {
      setValidationErrors((prevErrors) => ({ ...prevErrors, message: 'Please enter a Reason for Contact.' }));
      setHasError(true);
    } else if (!allowedSpecialCharacters.test(formData.Message)) {
      setValidationErrors((prevErrors) => ({...prevErrors, message: 'Reason for Contact has some invalid special characters. Only following special characters are allowed (-@#.*_)'}));
      setHasError(true);
    } else {
      setValidationErrors((prevErrors) => ({ ...prevErrors, message: '' }));
    }

    if (!collectionStatementChecked) {
      setValidationErrors((prevErrors) => ({ ...prevErrors, checkbox: true }));
      //   hasError = true;
      setHasError(true);
    } else {
      setValidationErrors((prevErrors) => ({ ...prevErrors, checkbox: false }));
    }
    const emailPattern =
      /^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/;
    if (!formData.Email) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        email: 'Email is required.',
      }));
      setHasError(true);
    } else if (!emailPattern.test(formData.Email)) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        email: 'Please enter a valid email address.',
      }));
      setHasError(true);
    } else {
      setValidationErrors((prevErrors) => ({ ...prevErrors, email: '' }));
    }
      // Set validation for phone number.  It will allow only numbers
    const phoneNumberPattern = /^[0-9]+$/;
      if (formData.Phone && !phoneNumberPattern.test(formData.Phone)) {
       setValidationErrors((prevErrors) => ({
         ...prevErrors,
         phone: 'Please enter a valid phone number.',
       }));
       setHasError(true);
     }
     else {
       setValidationErrors((prevErrors) => ({ ...prevErrors, phone: '' }));
    } 
    
    if (locale) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        Country: locale.toUpperCase(),
      }));
    }

    if (locale) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        Country: locale.toUpperCase(),
        Source_Site: Source_Site,
      }));
    }

  };

  // Commented this Logic because it is giving next_error Mayank is checking the logic for this lines of code
  // //Logic to check whether internet is available or not. 
  // //If Internet is not available then we can send user message "Your internet is down, Please try again later"
  // const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  // const updateOnlineStatus = () => {
  //   setIsOnline(navigator.onLine);
  // };
  // useEffect(() => {
  //   window.addEventListener('online', updateOnlineStatus);
  //   window.addEventListener('offline', updateOnlineStatus);
  //   // Cleanup event listeners on component unmount
  //   return () => {
  //     window.removeEventListener('online', updateOnlineStatus);
  //     window.removeEventListener('offline', updateOnlineStatus);
  //   };
  // }, []);
  // const internetOn = isOnline;
  
  
  useEffect(() => {
    if (!hasError) {
      handleAPICall();
    }
  }, [hasError, handleValidations]);
  const handleAPICall = async () => {

    // Commented this Logic because it is giving next_error Mayank is checking the logic for this lines of code
    // if(internetOn == false)
    //   {
    //     setError(
    //       formatMessage({
    //         id: 'query.error',
    //         defaultMessage: 'Your internet is down, Please try again later.',
    //       }),)
    //       return;
    //   }
    
    if (!hasError) {
      setHasError(true);
      try {
        const recaptchaValue = recaptchaRef.current?.getValue();
        // const secret = RECAPTCHA_SECRET_KEY;
        const response = await extensions.product.submitContactForm({ formData, recaptchaValue });
        

        if (!recaptchaValue) {
          setError('');
          setSuccess('');
          setReCaptchaError('reCaptcha verification failed. Please try again');
          return;
        }

        //When error exists in message then we will throw an error
        if (response.error && response.error.message === "Error") {
          setReCaptchaError('');
          setError('');
          setSuccess('');
          setError(
            formatMessage({
              id: 'query.error',
              defaultMessage: 'We are experiencing technical issues. Please try again later.',
          }),)
          return;
        }
        else if (!response.isError) {
          setError('');
          setReCaptchaError('');
          setSuccess(
            formatMessage({
              id: 'query.submitted',
              defaultMessage: 'Your query has been submitted.',
            }),
          );
          setFormData({
            First_Name: '',
            Purpose_of_Enquiry: '',
            Email: '',
            Occupation: '',
            Preferred_Contact_Method: '',
            Street_Address: '',
            Message: '',
            Phone: '',
            Country: '',
            Source_Site: '',
          });
          setCollectionStatementChecked(false);
          setClearDropdowns(true);
          recaptchaRef.current?.reset()
        } else {
          setReCaptchaError('');
          setError('');
          setSuccess('');
          //setReCaptchaError('reCaptcha verification failed. Please try again');
          setError(
            formatMessage({
              id: 'query.error',
              defaultMessage: 'We are experiencing technical issues. Please try again later.',
          }),)
        }  
      } catch (error) {
          setError(
            formatMessage({
              id: 'query.error',
              defaultMessage: 'We are experiencing technical issues. Please try again later.',
            }),)
      }
    }
  };
  const updateData = (name: string, value: string) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    // Clear error messages for inputs on change
    if (name === 'Message') {
      setValidationErrors((prevErrors) => ({ ...prevErrors, message: '' }));
    }
    if (name === 'Purpose_of_Enquiry') {
      setValidationErrors((prevErrors) => ({ ...prevErrors, enquiry: false }));
    }
    if (name === 'First_Name') {
      setValidationErrors((prevErrors) => ({ ...prevErrors, firstName: '' }));
    }
    if (name === 'Checkbox') {
      setValidationErrors((prevErrors) => ({ ...prevErrors, checkbox: false }));
    }
    if (name === 'Street_Address') {
      setValidationErrors((prevErrors) => ({ ...prevErrors, streetAddress: '' }));
    }    
    if (name === 'Phone') {
      setValidationErrors((prevErrors) => ({ ...prevErrors, phone: '' }));
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setClearDropdowns(false);
    updateData(e.target.name, e.target.value);
  };
  // const handleEnquirySelectChange = (option: Option) => {
  //   updateData('Purpose_of_Enquiry', option.value.toString());
  // };
  const handleEnquirySelectChange = (option: Option) => {
    setFormData((prevData) => ({
      ...prevData,
      Purpose_of_Enquiry: option.value.toString(),
    }));
    updateData('Purpose_of_Enquiry', option.value.toString());
  };
  const handleOccupationSelectChange = (option: Option) => {
    updateData('Occupation', option.value.toString());
  };
  const handleContactSelectChange = (option: Option) => {
    updateData('Preferred_Contact_Method', option.value.toString());
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setCollectionStatementChecked(isChecked);
    // Clear the error if the checkbox is checked
    if (isChecked) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        checkbox: false,
      }));
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  return (
   <>

      {isContactUsPage && (
        <div className="hidden px-24 pb-8 pt-16 md:block md:px-12 lg:px-32 xl:px-44 2xl:px-64">
          <Breadcrumb Separator={<ChevronRightIcon className="mt-2 w-16" />} listClassName="!gap-x-16">
            <Link link={'/'}>
              <HomeIcon className="mt-6 w-20" />
            </Link>
            <span className={`text-14 font-normal capitalize leading-4 text-[#666]`}>
              About Us
            </span>
            <span className={`text-14 font-normal capitalize leading-4 text-[#666]`}>
              Support
            </span>
          </Breadcrumb>
        </div>
      )}


      <div
        className={`${
          isTabletSize
            ? 'mb-50 flex min-h-[45rem] gap-80 px-24 lg:px-48 xl:gap-40 xl:px-60 2xl:gap-80 2xl:px-80 '
            : 'flex flex-col'
        } ${isDesktopSize ? 'flex-row-reverse' : 'flex-col-reverse'} ${
          data?.mobile_image_alignment === 'Top' ? 'flex-col' : ''
        }`}
      >
        <form
          onSubmit={handleValidations}
          noValidate
          className="flex flex-1 flex-col gap-15 px-24 py-33 md:gap-15 md:px-28 md:py-33 xl:gap-15 xl:px-36 xl:py-40 2xl:gap-16 2xl:px-64 2xl:py-80"
        >
          <Typography as="h1" className="pb-20 text-32 font-medium leading-10 text-black xl:text-34 2xl:text-48">
            {formatMessage({ id: 'Send us a message', defaultMessage: 'Send us a message' })}
          </Typography>
         
         
         
          <Dropdown
            label={'Purpose of Enquiry'}
            selectOptions={Purpose_of_Enquiry_Options}
            selectOnChange={handleEnquirySelectChange}
            disableFirstOption={true}
            clearDropdown={clearDropdowns}
            required
            selectDropdownClassNames="!text-16 !text-black text-left border-dim-gray overflow-hidden !h-fit !py-8 w-full"
            ></Dropdown>
          {validationErrors.enquiry && (
            <span className="pl-5 text-16 text-red-600" id="enquiryError">
              {formatMessage({
                id: 'purpose.of.enquiry.required',
                defaultMessage: 'Purpose of enquiry is required.',
              })}
            </span>
          )}





          <Input
            label={'Name'}
            type="text"
            required
            name="First_Name"
            maxLength={50}
            onChange={(e) => {
              handleChange(e);
              setValidationErrors((prevErrors) => ({ ...prevErrors, firstName: '' }));
            }}
            placeholder={`${formatMessage({
              id: 'first.name',
              defaultMessage: 'Name',
            })}`}
            value={formData.First_Name}
            autoComplete=""
            className="border-1 rounded !border-[#747474] !text-16 !text-black placeholder:text-black"
          />
          {validationErrors.firstName && (
            <span className="pl-5 text-16 text-red-600" id="firstNameError">
                {validationErrors.firstName}
            </span>
          )}
          <Input
            label={'Email'}
            name="Email"
            required
            pattern="^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$"
            title="Please enter a valid email address."
            type="email"
            onChange={(e) => {
              handleChange(e);
              setValidationErrors((prevErrors) => ({ ...prevErrors, email: '' }));
            }}
            placeholder={formatMessage({ id: 'email.address', defaultMessage: 'Email Address' })}
            value={formData.Email}
            autoComplete=""
            maxLength={100}
            className="border-1 relative rounded !border-[#747474] !text-16 !text-black placeholder:text-black"
          />
          {validationErrors.email && (
            <span className="pl-5 text-16 text-red-600" id="emailError">
              {validationErrors.email}
            </span>
          )}
          <Input
            label={'Street Address'}
            type="text"
            name="Street_Address"
            onChange={handleChange}
            placeholder="Street Address"
            value={formData.Street_Address}
            maxLength={250}
            autoComplete=""
            className="border-1 rounded !border-[#747474] !text-16 !text-black placeholder:text-black"
          />
          {validationErrors.streetAddress && (
            <span className="pl-5 text-16 text-red-600" id="streetAddressError">
              {validationErrors.streetAddress}
            </span>
          )}
          <Input
            label={"Phone"}
            type="text"
            name="Phone"
            pattern="[0-9]+"
            title="Please enter a valid phone number."
            onChange={(e) => {
              handleChange(e);
              setValidationErrors((prevErrors) => ({ ...prevErrors, phone: '' }));
            }}
            placeholder="Phone"
            value={formData.Phone}
            maxLength={12}
            autoComplete=""
            className="border-1 !text-black rounded !border-[#747474] !text-16 placeholder:text-black"
          />
          {validationErrors.phone && (
            <span className="text-16 text-red-600 pl-5" id="phoneError">
              {validationErrors.phone}
            </span>
          )}
          <Dropdown
            label={'Preferred Contact Method'}
            selectOptions={Preferred_Contact_Method_Options}
            selectOnChange={handleContactSelectChange}
            disableFirstOption={true}
            clearDropdown={clearDropdowns}
            selectDropdownClassNames="rounded !text-16 !text-black text-left border-1 !border-[#747474] overflow-hidden !h-fit !py-8 w-full"
          />
          <Dropdown
            label={'Occupation'}
            selectOptions={Occupation_Options}
            selectOnChange={handleOccupationSelectChange}
            disableFirstOption={true}
            clearDropdown={clearDropdowns}
            selectDropdownClassNames="rounded !text-16 !text-black text-left border-1 !border-[#747474] overflow-hidden !h-fit !py-8 w-full"
          />
          <div className="relative">
            <span className="text-16 font-medium text-secondary-black">
              Reason for Contact
              <span className="!text-red-500"> *</span>
            </span>
            <textarea
              // label={"Message"}
              name="Message"
              required
              onChange={(e) => {
                handleChange(e);
                setValidationErrors((prevErrors) => ({ ...prevErrors, message: '' }));
              }}
              placeholder="Describe your query here ... (Max 300 characters)"
              value={formData.Message}
              maxLength={300}
              autoComplete=""
              className="border-1 mt-8 h-100 w-full rounded !border-[#747474] px-12 py-10 text-left !text-16 !text-black placeholder:text-black"
            />
          </div>
          {validationErrors.message && (
            <span className="pl-5 text-16 text-red-600" id="descriptionError">
              {validationErrors.message}
            </span>
          )}
          <div className="">
            <label className="text-16 font-medium tracking-wide text-secondary-black">
              {formatMessage({
                id: 'privacy.consent',
                defaultMessage: 'Privacy consent',
              })}
              <span className="!text-red-500"> * </span>
            </label>
            <div className="flex items-baseline gap-16">
              <Input
                // label={"Consent"}
                type="checkbox"
                name="checkbox"
                required
                className="!h-18 !w-18 !p-0"
                checked={collectionStatementChecked}
                onChange={handleCheckboxChange}
              />
              <Markdown
                markdown={collectionStatement?.description}
                className="!text-16 !leading-6 !text-black [&>p>a]:!text-[#337ab7]"
                onCollectionLinkClick={openModal}
              />
            </div>
          </div>
          {validationErrors.checkbox && (
            <span className="inline-block pl-5 text-16 text-red-600" id="checkboxError">
              {formatMessage({
                id: 'please.check',
                defaultMessage: 'Please check the box if you want to proceed',
              })}
            </span>
          )}
          <ReCAPTCHA ref={recaptchaRef} sitekey={`${RECAPTCHA_SITE_KEY}`} onChange={onChangeRecaptcha}        />
          {success && (
            <div>
              <span className="text-16 text-green-600">{success}</span>
            </div>
          )}

          {/* Added error message if API failed */}
          {error && (
            <div>
              <span className="text-16 text-red-600">{error}</span>
            </div>
          )}


          {reCaptchaError && (
            <div>
              <span className="text-16 text-red-600">{reCaptchaError}</span>
            </div>
          )}
          <Button
            type="submit"
            className="border-1 w-full rounded-none border border-[#2B2C39] bg-white px-18 py-10 shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4] md:w-fit md:px-32 md:py-18"
          >
            <Typography as="span" className="text-18 text-[#090909]">
              {formatMessage({ id: 'submit', defaultMessage: 'Submit' })}
            </Typography>
          </Button>
        </form>
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            preventScroll={true}
            style={{
              content: {
                backgroundColor: 'white',
                padding: isLargeScreen ? '60px 40px 10px 40px' : '40px 20px 10px 40px',
                overflow: 'hidden',
              },
            }}
            closeButton
            className='border-transparent focus:border-transparent focus:ring-0 focus:outline-none'
            closeTimeoutMS={200}
          >
            <div style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', overflowX: 'hidden' }} className="modal-height p-4">
              <Markdown
                markdown={PopUPModalCollectionStatement?.description}
                className="markdown-content [&>div>div>p>a]:!text-[#337ab7]"
              />
            </div>
          </Modal>
          
        )}

        <div className="flex flex-1 flex-wrap items-center justify-center bg-[#F7F7F7] px-60 py-80 text-center xl:px-30 xl:py-40 2xl:px-60 2xl:py-40">
          <div>
            {contact?.title && (
              <Typography
                as="h1"
                className="pb-32 text-32 font-medium text-black xl:pb-24 xl:text-34 2xl:pb-32 2xl:text-48"
              >
                {contact?.title}
              </Typography>
            )}
            {contact?.description && (
              <Markdown
                markdown={contact?.description}
                className="w-271 text-20 font-normal text-[#4D4D4D] md:w-268 lg:w-344 xl:text-18 2xl:text-20 md:[&>*:nth-child(1)]:mr-8 md:[&>*:nth-child(1)]:inline lg:[&>*:nth-child(1)]:mr-8 lg:[&>*:nth-child(1)]:inline lg:[&>*:nth-child(2)]:inline"
              />
            )}
          </div>
        </div>
      </div>

    </>
    
  );
};
export default ContactSlider;